import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import adityaPhoto from '../assets/aditya-ghiya.jpeg';
import ayushiPhoto from '../assets/ayushi-gupta.jpeg';

gsap.registerPlugin(ScrollTrigger);
const CALENDAR_URL = 'https://calendar.app.google/DtBNRYcoX9a3oD5a9';

const StatBox = ({ value, suffix, label, color }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obj = { val: 0 };
    const st = ScrollTrigger.create({
      trigger: ref.current,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(obj, {
          val: value,
          duration: 2,
          ease: 'power2.out',
          onUpdate() {
            setDisplay(Math.floor(obj.val));
          },
        });
      },
    });
    return () => st.kill();
  }, [value]);

  return (
    <div
      ref={ref}
      data-testid={`stat-box-${label.toLowerCase().replace(/\s/g, '-')}`}
      className="text-center p-6 rounded-2xl"
      style={{ backgroundColor: '#F0F5FF' }}
    >
      <div
        className="text-4xl sm:text-5xl font-bold mb-1"
        style={{ color: color || '#2D5A8C', fontFamily: "'Outfit', sans-serif" }}
      >
        {display}{suffix}
      </div>
      <div className="text-sm font-medium" style={{ color: '#6B7280' }}>
        {label}
      </div>
    </div>
  );
};

const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const content = sectionRef.current?.querySelector('.about-content');
    if (!content) return;

    content.style.opacity = '0';
    content.style.transform = 'translateX(-24px)';
    content.style.transition = 'opacity 0.8s ease, transform 0.8s ease';

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          content.style.opacity = '1';
          content.style.transform = 'translateX(0)';
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  const partners = [
    {
      photo: adityaPhoto,
      name: 'CA Aditya Ghiya',
      title: 'Founding Partner',
      bio: 'Aditya Ghiya, having qualified in Nov 2015, has experience (10+ years) of working in the Taxation domain–Domestic and International, Accounting and Auditing. 100% sincerity and hard work to give best possible services to all clients is what forms part of his contribution at the firm. His vision is to grow in the field of UK and India taxation, accounting and compliances, and provide best quality work to the clients by putting his capabilities and soft skills to the best use and grow in collaboration with the experts in the field. His ability to understand the business environment coupled with his sound IT, financial and accounting knowledge background has enabled him to specialize in streamlining and developing critical business processes of companies. Prior to working as practicing CA, Aditya has worked with Ernst & Young, Mumbai and interned with Chhajed & Doshi, Mumbai.',
    },
    {
      photo: ayushiPhoto,
      name: 'CA Ayushi Ghiya',
      title: 'Founding Partner',
      bio: 'Ayushi, having qualified in May 2019, has varied experience in the field of domestic taxation, Pension transfer (QROPS), supervision of books of accounts, audit and management. Rich in client communication and staff management makes her an integral part of the organization. Exceeding client’s expectations is what she’s always passionate about.',
    },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="about-section"
      id="about"
      aria-label="About Aditya Ghiya & Associates"
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: '#FAFAFA' }}
    >
      {/* Subtle diagonal lines */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(135deg, #2D5A8C0C 0px, #2D5A8C0C 1px, transparent 1px, transparent 24px)',
          opacity: 1,
        }}
      />
      {/* Soft teal glow top-right */}
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #1B8A9612 0%, transparent 70%)' }}
      />
      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Text */}
          <div className="about-content space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#2D5A8C' }}>
                About Us
              </p>
              <h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
              >
                Why Aditya Ghiya & Associates
              </h2>
            </div>
            <p className="text-base leading-relaxed" style={{ color: '#6B7280' }}>
              We are a specialized cross-border taxation practice with deep expertise in India-UK financial matters. Our team of CA India and ICAEW qualified professionals brings together the best of both jurisdictions to serve expats, NRIs, small businesses, and high-net-worth individuals.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#6B7280' }}>
              From eliminating double taxation through DTAA benefits to structuring businesses for optimal cross-border compliance, we align your global income strategy with precision and care. Our approach is personal, data-driven, and always in your best financial interest.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#6B7280' }}>
              Whether you are relocating between India and the UK, managing overseas assets, or building a cross-border business, Aditya Ghiya & Associates is your trusted financial partner across both jurisdictions.
            </p>
            <div className="pt-2">
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: '#2D5A8C' }}
              >
                Schedule a Free Consultation
              </a>
            </div>
          </div>

          {/* Right: Stats */}
          <div className="grid grid-cols-1 gap-5">
            <StatBox value={8} suffix="+" label="Years of Experience" color="#2D5A8C" />
            <StatBox value={1200} suffix="+" label="Clients Served" color="#F59E0B" />
            <StatBox value={2} suffix="" label="Jurisdictions (India & UK)" color="#1B8A96" />
          </div>
        </div>

        <div className="mt-16">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#2D5A8C' }}>
              Founding Partners
            </p>
            <h3 className="text-3xl sm:text-4xl font-bold" style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}>
              Meet our founding leadership
            </h3>
          </div>

          <div className="grid gap-6 w-full">
            {partners.map(({ photo, name, title, bio }, index) => (
              <div
                key={name}
                className={`rounded-[2rem] overflow-hidden bg-white border border-slate-200 shadow-[0_18px_60px_rgba(15,23,42,0.08)] w-full flex flex-col md:flex-row ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className="flex-shrink-0 p-4 md:p-6 flex items-start justify-center w-full md:w-auto">
                  <div className="w-full md:w-44 aspect-square rounded-3xl overflow-hidden bg-slate-100 shadow-inner">
                    <img
                      src={photo}
                      alt={`${name} headshot`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="p-4 md:p-6 flex-1 flex flex-col justify-center">
                  <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
                  <h4 className="text-2xl font-bold mb-4" style={{ color: '#111827' }}>
                    {name}
                  </h4>
                  <p className="text-sm leading-7 text-slate-600">
                    {bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
